import React, {useState, useEffect, useRef, useCallback} from 'react'
import {AdvancedMarker, APIProvider, InfoWindow, Map, Pin} from '@vis.gl/react-google-maps'
import PropTypes from 'prop-types'
import ServiceCenterTooltip from './service-center-tooltip'
import {Box} from '@chakra-ui/react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {remCalc} from '../../theme/_utils'
import {BRAND_KEY} from '../../brand'
import PickupPointTooltip from './pickup-point-tooltip'

const LocationMap = ({...props}) => {
    const [infoWindow, setInfoWindow] = useState({})
    const [infoWindowShown, setInfoWindowShown] = useState(false)
    const {customParams} = getConfig()
    const INITIAL_CAMERA = {
        center: {
            lat: props?.selectedStore?.latitude || props?.fallbackCoordinates?.latitude || 40,
            lng: props?.selectedStore?.longitude || props?.fallbackCoordinates?.longitude || 0,
        },
        zoom: 5,
    }
    const [cameraProps, setCameraProps] = useState(INITIAL_CAMERA)

    const useStyleInfoWindow = () => {
        useEffect(() => {
            const style = document.createElement('style')
            style.innerHTML = `
            .gm-style {
              font: inherit !important
              color: oklch(22.61% 0.065 273.7) !important
            }
            .gm-style-iw {
              max-width: ${remCalc(330)} !important;
              border-radius: large !important;
              border: 1px solid var(--chakra-colors-border-neutral-highlight) !important;
              box-shadow: 0 0 0 1px var(--chakra-colors-border-neutral-highlight) inset, 0 1px 2px 0 var(--chakra-shadows-neutral-xs), 0 0 0 1px var(--chakra-shadows-neutral-xs) !important;
            }
              .gm-style-iw-d {
              overflow: hidden !important;
               
            }
            .gm-style-iw-ch {
                padding-top: 0;
                color: oklch(22.61% 0.065 273.7); !important;
            }
            .gm-style .gm-style-iw-c {
                padding: ${remCalc(16)} !important;
                background-color: oklch(94.68% 0.0046 78.3) !important;
            }
            .gm-style-iw-chr {
                align-items: center;
                justify-content: space-between;
                gap: ${remCalc(8)} !important;
            }
            .gm-style-iw-tc :after{
                background-color: oklch(94.68% 0.0046 78.3) !important;
            }
            .gm-ui-hover-effect {
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
                width: ${remCalc(22)} !important;
                height: ${remCalc(22)} !important;
                padding: ${remCalc(3)} !important;
                align-self: flex-start !important;
            }
            .gm-ui-hover-effect > span {
                width: ${remCalc(22)} !important;
                height: ${remCalc(22)} !important;
                padding: ${remCalc(3)} !important;
                flex-shrink: 0 !important;
                margin: 0 !important;
            }
            @media (min-width: 768px) {
                .gm-style-iw {
                    max-width: ${remCalc(342)} !important;
                }
            }
          `
            document.head.appendChild(style)

            return () => {
                document.head.removeChild(style)
            }
        }, [])
    }
    useStyleInfoWindow()

    useEffect(() => {
        const hasSelection = Boolean(props.selectedStore && Object.keys(props.selectedStore).length)

        if (hasSelection) {
            setInfoWindow(props.selectedStore)
            setInfoWindowShown(true)
        } else {
            setInfoWindowShown(false)
            setInfoWindow({})
        }
    }, [props.selectedStore])

    useEffect(() => {
        if (props.searchedLocation && Object.keys(props.searchedLocation).length > 0) {
            setCameraProps({
                center: {
                    lat: props.searchedLocation?.lat,
                    lng: props.searchedLocation?.lng,
                },
                zoom: props.searchedLocation?.zoom || 9,
            })
        }
    }, [props.searchedLocation])

    const handleCameraChange = useCallback((ev) => {
        setCameraProps(ev.detail)
    }, [])

    const markerRefs = useRef({})

    const handleMarkerClick = useCallback(
        (store) => {
            // Focus on the marker when clicked
            props.setSelectedStore(store)
            markerRefs.current[store.id]?.focus()
            setTimeout(() => {
                setInfoWindowShown(true)
            }, 1000)
        },
        [props.setSelectedStore],
    )

    const handleInfoWindowClose = useCallback(() => {
        setInfoWindowShown(false)
        props.setSelectedStore({})
        setInfoWindow({})
        markerRefs.current[props.selectedStore?.id]?.blur()
    }, [props.setSelectedStore, props.selectedStore?.id])

    return (
        <APIProvider apiKey={customParams.googleMapsApiKey} libraries={['marker', 'places']}>
            <Map
                {...cameraProps}
                onCameraChanged={handleCameraChange}
                onDragstart={handleInfoWindowClose}
                gestureHandling={'greedy'}
                disableDefaultUI
                height={remCalc(572)}
                options={{
                    mapId: customParams.googleMapsId,
                    mapTypeControl: false,
                    fullscreenControl: true,
                    zoomControl: true,
                    cameraControl: true,
                    streetViewControl: false,
                }}
            >
                {props.stores.map((store, index) => (
                    <Box key={store.id} onClick={() => handleMarkerClick(store)}>
                        <AdvancedMarker
                            key={store.id}
                            ref={(ref) => (markerRefs.current[store.id] = ref)}
                            position={{
                                lat: store?.latitude,
                                lng: store?.longitude,
                            }}
                            title={'AdvancedMarker with customized pin.'}
                        >
                            <Pin
                                background={BRAND_KEY === 'kenwood' ? '#D11B59' : 'black'}
                                borderColor={BRAND_KEY === 'kenwood' ? '#D11B59' : '#1e89a1'}
                                glyphText={String(index + 1) || ''}
                                glyphColor={'white'}
                            ></Pin>
                        </AdvancedMarker>
                    </Box>
                ))}

                {infoWindowShown && (
                    <InfoWindow
                        position={{
                            lat: props?.selectedStore?.latitude || 40,
                            lng: props?.selectedStore?.longitude || 0,
                        }}
                        anchor={markerRefs.current[props.selectedStore?.id]}
                        disableAutoPan={false}
                        shouldFocus={true}
                        onCloseClick={() => {
                            handleInfoWindowClose()
                        }}
                        headerContent={
                            <Box textStyle={'head-xsmall'} fontWeight="medium">
                                {infoWindow?.name}
                            </Box>
                        }
                        className="dl-info-window"
                    >
                        {props.isPickup
                            ? props.selectedStore &&
                              Object.keys(props.selectedStore).length > 0 && (
                                  <PickupPointTooltip
                                      selectedStore={infoWindow}
                                      onConfirm={props.onConfirm}
                                  />
                              )
                            : props.selectedStore &&
                              Object.keys(props.selectedStore).length > 0 && (
                                  <ServiceCenterTooltip selectedStore={infoWindow} />
                              )}
                    </InfoWindow>
                )}
            </Map>
        </APIProvider>
    )
}

LocationMap.propTypes = {
    selectedStore: PropTypes.object,
    fallbackCoordinates: PropTypes.object,
    stores: PropTypes.array,
    setSelectedStore: PropTypes.func,
    searchedLocation: PropTypes.object,
    isPickup: PropTypes.bool,
    onConfirm: PropTypes.func,
}

export default LocationMap
